import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { usePartnerCode } from '#pplx/frontend/apps/ask/discount/usePartnerCode.js';
import { usePartnerDiscountCode } from '#pplx/frontend/apps/ask/discount/usePartnerDiscountCode.js';
import { useDiscountCode } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useDiscountCode.js';
import { resolveAppBannerPlan } from '#pplx/frontend/apps/ask/layout/shell/banner/resolveAppBannerPlan.js';
import { usePendingInvitation } from '#pplx/frontend/apps/ask/organization/hooks/usePendingInvitation.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { BannerUpsellSlot } from '#pplx/frontend/apps/ask/upsell-slots/banner/BannerUpsellSlot.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { ErrorBoundary } from '#pplx/frontend/platform-components/ErrorBoundary.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useMemo } from 'react';
const DiscountCodeBanner = Dynamic(async () => {
    const { DiscountCodeBanner } = await import('#pplx/frontend/apps/ask/subscription-ui/DiscountCodeBanner.js');
    return { default: DiscountCodeBanner };
});
const PartnerBanner = Dynamic(async () => {
    const { PartnerBanner } = await import('#pplx/frontend/apps/ask/subscription-ui/PartnerBanner.js');
    return { default: PartnerBanner };
});
const OrgUpgradeBillingBanner = Dynamic(async () => {
    const { OrgUpgradeBillingBanner } = await import('#pplx/frontend/apps/ask/organization/components/settings/about/OrgBillingBanner.js');
    return { default: OrgUpgradeBillingBanner };
});
const hideOrgUpgradeBillingBanner = (pathname) => {
    const HIDE_UPGRADE_BANNER_PATHS = ['/onboarding/org/create'];
    return HIDE_UPGRADE_BANNER_PATHS.includes(pathname);
};
function AppBannerInner() {
    const reason = 'app-banner-inner';
    const { isMobileUserAgent } = useIsMobile();
    const { message, discountCode, clearDiscountCode } = useDiscountCode();
    const { hasAccessToProFeatures, isEnterprise: hasActiveEnterpriseSubscription } = useIsSubscribed();
    const isEnterpriseUser = useIsEnterpriseUser();
    const [partnerCode] = usePartnerCode();
    const [partnerDiscountCode] = usePartnerDiscountCode();
    const pathname = usePathname();
    const { organization, orgUser } = useUserOrgData({
        reason,
    });
    const { pendingInvitation } = usePendingInvitation({
        reason,
    });
    const isUnpaidOrg = isEnterpriseUser && !hasActiveEnterpriseSubscription;
    const isSalesforceNetsuiteBilling = organization?.subscription_source === 'salesforce_netsuite';
    const isAdmin = orgUser?.role === 'ADMIN';
    const plan = resolveAppBannerPlan({
        pathname: pathname ?? '',
        hasPendingOrgInvite: pendingInvitation != null && !organization && !orgUser,
        canShowOrgBilling: isAdmin &&
            isUnpaidOrg &&
            !isSalesforceNetsuiteBilling &&
            !hideOrgUpgradeBillingBanner(pathname ?? '') &&
            !isMobileUserAgent,
        hasPartnerCode: Boolean(partnerCode),
        hasDiscountCode: Boolean(discountCode),
        hasAccessToProFeatures,
    });
    let primaryBannerContent = null;
    switch (plan.primary) {
        case 'org-billing':
            primaryBannerContent = (_jsx(OrgUpgradeBillingBanner, { orgStripeStatus: organization?.stripe_status }));
            break;
        case 'partner':
            primaryBannerContent = partnerCode ? (_jsx(PartnerBanner, { partnerCode: partnerCode, partnerDiscountCode: partnerDiscountCode })) : null;
            break;
        case 'discount':
            primaryBannerContent = discountCode ? (_jsx(DiscountCodeBanner, { message: message, discountCode: discountCode, clearDiscountCode: clearDiscountCode })) : null;
            break;
    }
    const { enabled: upsellEnabled, hasPrimarySurfaceContent } = plan.upsell;
    const bannerUpsellCallsite = useMemo(() => ({
        enabled: upsellEnabled,
        pathname,
        routeKind: pathname === '/' ? 'home' : 'other',
        surface: {
            hasPrimarySurfaceContent,
        },
    }), [pathname, upsellEnabled, hasPrimarySurfaceContent]);
    return (_jsxs(_Fragment, { children: [_jsx(BannerUpsellSlot, { callsite: bannerUpsellCallsite }), primaryBannerContent] }));
}
export default function AppBanner() {
    return (_jsx(ErrorBoundary, { fallback: null, code: "APP_BANNER_ERROR", children: _jsx(AppBannerInner, {}) }));
}
//# sourceMappingURL=AppBanner.js.map